import type { Budget, BudgetSettings } from '../types/budget';
import type { StorageData } from '../db/index';
import type { VersioningState, BudgetVersionHistory } from '../types/versioning';
import { BUDGET_DATA_UPDATED_EVENT, VERSIONING_UPDATED_EVENT } from '../constants/events';
import { normalizeResponsible, normalizeDestination } from '../utils/normalization';

const STORAGE_KEY = 'budget_app_data';
const VERSIONING_STORAGE_KEY = 'budget_app_versioning';
const CONVEX_ID_MAP_KEY = 'budget_app_convex_id_map';
const CONVEX_IMPORT_DONE_KEY = 'budget_app_convex_import_done';
const LAST_SYNCED_USER_KEY = 'budget_app_last_synced_user_id';

export type ConvexBudgetDoc = {
  _id: string;
  _creationTime?: number;
  userId: string;
  id?: number;
  name: string;
  date: number;
  transactions: Array<{
    id?: number;
    category: string;
    responsible: string;
    description: string;
    destination: string;
    amount: number;
  }>;
  categories: Array<{ id: string; name: string }>;
  responsibles: Array<{ id: string; name: string; isProtected?: boolean }>;
  destinations: Array<{ id: string; name: string; isProtected?: boolean }>;
  settings: { contributionCalculationType: string; currency: string };
  updatedAt: number;
  /** Server-assigned monotonic revision used for echo detection (absent on legacy docs) */
  rev?: number;
};

export function getConvexIdMap(): Record<number, string> {
  try {
    const raw = localStorage.getItem(CONVEX_ID_MAP_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export function setConvexIdMap(map: Record<number, string>): void {
  localStorage.setItem(CONVEX_ID_MAP_KEY, JSON.stringify(map));
}

export function isConvexImportDone(): boolean {
  return localStorage.getItem(CONVEX_IMPORT_DONE_KEY) === '1';
}

export function setConvexImportDone(): void {
  localStorage.setItem(CONVEX_IMPORT_DONE_KEY, '1');
}

export function clearConvexImportDone(): void {
  localStorage.removeItem(CONVEX_IMPORT_DONE_KEY);
}

/**
 * The user ID this browser last synced with. Persists across sign-out so an
 * account switch can be detected on the next sign-in (and local data is then
 * never silently pushed into the other account).
 */
export function getLastSyncedUserId(): string | null {
  return localStorage.getItem(LAST_SYNCED_USER_KEY);
}

export function setLastSyncedUserId(userId: string): void {
  localStorage.setItem(LAST_SYNCED_USER_KEY, userId);
}

export function convexDocToBudget(doc: ConvexBudgetDoc, localId: number): Budget {
  return {
    id: localId,
    name: doc.name,
    date: new Date(doc.date),
    transactions: doc.transactions.map((t) => ({
      ...t,
      responsible: normalizeResponsible(String(t.responsible)),
      destination: normalizeDestination(String(t.destination)),
    })),
    categories: doc.categories.map((c) => ({ ...c })),
    responsibles: doc.responsibles.map((r) => ({ ...r })),
    destinations: doc.destinations.map((d) => ({ ...d })),
    settings: { ...doc.settings } as BudgetSettings,
  };
}

export function budgetToConvexDoc(budget: Budget): Omit<ConvexBudgetDoc, '_id' | '_creationTime' | 'userId' | 'updatedAt'> {
  // Convert date to timestamp, handling all possible formats
  let dateTimestamp: number;
  if (budget.date instanceof Date) {
    dateTimestamp = budget.date.getTime();
  } else if (typeof budget.date === 'number') {
    dateTimestamp = budget.date;
  } else if (typeof budget.date === 'string') {
    dateTimestamp = new Date(budget.date).getTime();
  } else {
    console.warn('Invalid date format, using current timestamp:', budget.date);
    dateTimestamp = Date.now();
  }

  return {
    id: budget.id,
    name: budget.name,
    date: dateTimestamp,
    transactions: (budget.transactions || []).map((t) => ({
      id: t.id,
      category: t.category,
      responsible: t.responsible,
      description: t.description,
      destination: t.destination,
      amount: t.amount,
    })),
    categories: (budget.categories || []).map((c) => ({ id: c.id, name: c.name })),
    responsibles: (budget.responsibles || []).map((r) => ({
      id: r.id,
      name: r.name,
      isProtected: r.isProtected,
    })),
    destinations: (budget.destinations || []).map((d) => ({
      id: d.id,
      name: d.name,
      isProtected: d.isProtected,
    })),
    settings: {
      contributionCalculationType: budget.settings.contributionCalculationType,
      currency: budget.settings.currency,
    },
  };
}

export function writeStorageData(data: StorageData, skipEvent?: boolean): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  if (!skipEvent) {
    window.dispatchEvent(
      new CustomEvent(BUDGET_DATA_UPDATED_EVENT, {
        detail: { timestamp: new Date(), source: 'convex' },
      })
    );
  }
}

export function getStorageDataRaw(): StorageData {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return { budgets: [], defaultBudgetId: null };
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { budgets: [], defaultBudgetId: null };
    const budgets = Array.isArray(parsed.budgets) ? parsed.budgets : [];
    return {
      budgets,
      defaultBudgetId: parsed.defaultBudgetId ?? null,
    };
  } catch {
    return { budgets: [], defaultBudgetId: null };
  }
}

// ===== Version History Sync Utilities =====

export function getVersioningState(): VersioningState {
  try {
    const raw = localStorage.getItem(VERSIONING_STORAGE_KEY);
    if (!raw) return { histories: {} };
    const parsed = JSON.parse(raw);

    // Convert timestamp strings back to Date objects
    const processedHistories: Record<number, BudgetVersionHistory> = {};
    Object.entries(parsed.histories || {}).forEach(([budgetId, history]: [string, unknown]) => {
      const h = history as BudgetVersionHistory;
      processedHistories[parseInt(budgetId)] = {
        ...h,
        versions: (h.versions || []).map((version) => ({
          ...version,
          timestamp: new Date(version.timestamp),
        })),
      };
    });

    return { histories: processedHistories };
  } catch {
    return { histories: {} };
  }
}

export function setVersioningState(state: VersioningState, skipEvent?: boolean): void {
  try {
    localStorage.setItem(VERSIONING_STORAGE_KEY, JSON.stringify(state));
    if (!skipEvent) {
      // Dispatch event so the UI can react to version history changes
      window.dispatchEvent(
        new CustomEvent(VERSIONING_UPDATED_EVENT, {
          detail: { timestamp: new Date(), source: 'convex' },
        })
      );
    }
  } catch (error) {
    console.error('Error saving versioning state:', error);
  }
}
